/**
 * OTP auth hook for PWA/Capacitor login flow.
 * Session ID persists in BOTH localStorage AND URL hash for reliability.
 */

import { useState, useCallback } from 'react'
import { useAuthStore } from '@turnifly/shared/stores'
import { verifyCode, saveTokens } from '@turnifly/shared/api'

const BOT_USERNAME = 'TurniflyBot'

function generateSessionId(): string {
  return crypto.randomUUID()
}

/**
 * Get session ID from multiple sources (most reliable first):
 * 1. URL hash (#session=xxx) — survives app reopens
 * 2. localStorage — survives page refreshes
 */
function getSavedSession(): string | null {
  // Check URL hash first
  const hash = window.location.hash
  const match = hash.match(/session=([a-f0-9-]+)/)
  if (match) return match[1]

  // Check localStorage
  return localStorage.getItem('turnifly_otp_session')
}

function saveSession(sid: string): void {
  localStorage.setItem('turnifly_otp_session', sid)
  // Also save to URL hash so it survives reopening the app
  window.location.hash = `session=${sid}`
}

function clearSession(): void {
  localStorage.removeItem('turnifly_otp_session')
  window.location.hash = ''
}

interface UseOtpAuthReturn {
  sessionId: string | null
  requestOtp: () => string
  verifyOtp: (code: string) => Promise<boolean>
  isLoading: boolean
  error: string | null
  clearError: () => void
}

export function useOtpAuth(): UseOtpAuthReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(getSavedSession)
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated)
  const setNewUser = useAuthStore((s) => s.setNewUser)

  const requestOtp = useCallback((): string => {
    const sid = generateSessionId()
    setSessionId(sid)
    setError(null)
    saveSession(sid)

    // Open Telegram bot deeplink
    const tgLink = `https://t.me/${BOT_USERNAME}?start=login_${sid}`
    window.open(tgLink, '_blank')

    return sid
  }, [])

  const verifyOtp = useCallback(async (code: string): Promise<boolean> => {
    const currentSession = sessionId ?? getSavedSession()
    if (!currentSession) {
      setError('Session topilmadi. Qaytadan kod oling.')
      return false
    }

    setIsLoading(true)
    setError(null)

    try {
      const res = await verifyCode(currentSession, code)
      saveTokens(res.access_token, res.refresh_token)
      setAuthenticated(true)
      setNewUser(!res.user.onboarding_completed)
      clearSession()
      return true
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Kod noto\'g\'ri'
      const errorMessages: Record<string, string> = {
        SESSION_NOT_FOUND: 'Session topilmadi. Qaytadan kod oling.',
        BLOCKED: 'Juda ko\'p urinish. 5 daqiqa kuting.',
        CODE_EXPIRED: 'Kod muddati o\'tgan. Yangi kod oling.',
        WRONG_CODE: 'Kod noto\'g\'ri. Qaytadan urinib ko\'ring.',
        TOO_MANY_ATTEMPTS: 'Juda ko\'p xato. 5 daqiqa kuting.',
      }
      setError(errorMessages[message] ?? `Xatolik: ${message}`)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [sessionId, setAuthenticated, setNewUser])

  const clearError = useCallback((): void => {
    setError(null)
  }, [])

  return { sessionId, requestOtp, verifyOtp, isLoading, error, clearError }
}
